/**
 * TargetedSupportApp component is the main application component.
 * Manages state for tabs, filters, and renders the appropriate views.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GuardrailTree as ApiGuardrailTree,
  type ProductTree,
} from "@financial-advisory/data-access/api-types";
import {
  useGetGuardrailTreesQuery,
  useGetProcessedClausesQuery,
  useGetProductTreesQuery,
  usePrefetchGuardrailEditData,
  usePrefetchProcessedClauseApi,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  type GuardrailTree,
  HOME_SIDEBAR_ITEMS,
  HOME_TAB,
  type OutcomeTree,
  type OverviewItem,
  type Tab,
  createTabFromItem,
  sidebarIcons,
} from "@targeted-support/ui/features/overview";
import {
  GraphView,
  GuardrailsSection,
  OutcomesSection,
  OverviewSection,
} from "@targeted-support/ui/features/overview";
import { TargetedSupportLayout } from "@targeted-support/ui/layouts";
import { Shield, Target } from "lucide-react";
import {
  cleanTreeNames,
  deduplicateTreesByQuestion,
  getTreesByMatchName,
} from "@unlikelyai-magic/utils";
import { GuardrailContent } from "./GuardrailContent";
import { BenchmarkPage } from "./benchmark/BenchmarkPage";
import { ConfirmDialog } from "./test/ConfirmDialog";
import { PastConversationsPage } from "./test/PastConversationsPage";
import { type PreviousConversation } from "./test/TestPage2";
import { TestPage2 } from "./test/TestPage2";

const VIEWS = {
  OVERVIEW: {
    id: "overview",
  } as const,
  OUTCOMES: {
    id: "outcomes",
  } as const,
  GUARDRAILS: {
    id: "guardrails",
  } as const,
  TEST: {
    id: "test",
  } as const,
  HISTORY: {
    id: "history",
  } as const,
  BENCHMARK: {
    id: "benchmark",
  } as const,
} as const;

/**
 * Extracts the document name from a guardrail name.
 * Examples:
 *   "COBS 4.2.1" -> "COBS4"
 *   "COBS 9B.1.2" -> "COBS9B"
 *   "PERG 8.4.3" -> "PERG" (special case - no numbered sub-documents)
 */
const extractDocumentName = (guardrailName: string): string => {
  const match = guardrailName.match(/^([A-Z]+)(?:\s+(\d+[A-Z]?))?/);
  if (!match) return "";

  if (match[1] === "PERG") {
    return "PERG";
  }
  const fullDocumentName = `${match[1]}${match[2] || ""}`;
  if (fullDocumentName === "COBS9B") {
    return "cp25-17";
  }
  return fullDocumentName;
};

type AvailableViews = (typeof VIEWS)[keyof typeof VIEWS]["id"];

const formatAsGuardrailTree = (tree: ApiGuardrailTree): GuardrailTree => {
  return {
    type: "guardrail",
    id: tree.id,
    title: tree.name,
    template: tree.template,
    guardrailType: tree.type,
  };
};

const formatAsOutcomeTree = (tree: ProductTree): OutcomeTree => {
  return {
    type: "outcome",
    id: tree.id,
    title: tree.name,
    template: tree.template,
    description: tree.description || undefined,
  };
};

/**
 * TargetedSupportApp component that manages the main application state and rendering.
 * Handles tab management, filtering, and navigation between different views.
 *
 * @returns The complete targeted support application
 */
export const TargetedSupportApp = () => {
  const [tabs, setTabs] = useState<Tab[]>([HOME_TAB]);
  const [activeTabId, setActiveTabId] = useState<string>(HOME_TAB.id);
  const [activeView, setActiveView] = useState<AvailableViews>(
    VIEWS.OVERVIEW.id
  );
  const [previousConversation, setPreviousConversation] = useState<
    PreviousConversation | undefined
  >(undefined);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const isSimulationRunningRef = useRef(false);
  const [pendingNavAction, setPendingNavAction] = useState<(() => void) | null>(
    null
  );
  const stopSimulationRef = useRef<(() => void) | null>(null);

  const handleSimulationRunningChange = useCallback((running: boolean) => {
    isSimulationRunningRef.current = running;
  }, []);

  const guardNavigation = useCallback(
    (action: () => void) => {
      if (isSimulationRunningRef.current && activeView === VIEWS.TEST.id) {
        setPendingNavAction(() => action);
        return;
      }
      action();
    },
    [activeView]
  );

  const handleConfirmLeave = useCallback(() => {
    stopSimulationRef.current?.();
    pendingNavAction?.();
    setPendingNavAction(null);
  }, [pendingNavAction]);

  const handleCancelLeave = useCallback(() => {
    setPendingNavAction(null);
  }, []);

  const { user } = useAuth0();
  const isAdmin = user?.email?.endsWith("@unlikely.ai") ?? false;

  const sidebarItems = useMemo(
    () =>
      isAdmin
        ? HOME_SIDEBAR_ITEMS
        : HOME_SIDEBAR_ITEMS.filter((item) => item.id !== "benchmark"),
    [isAdmin]
  );

  // Fetch all product trees to use for graph rendering
  const { data: productTreesData, isLoading: isProductTreesLoading } =
    useGetProductTreesQuery(null);
  const { data: guardrailTrees, isLoading: isGuardrailTreesLoading } =
    useGetGuardrailTreesQuery();
  const { data: processedClauses } = useGetProcessedClausesQuery();

  /**
   * Prepare trees the same way as TestPage2, so productTreesById matches what the
   * chat uses.
   *
   * Situation and segment trees still exist and still feed evaluation, but they
   * are no longer a browsable category: the Outcomes page lists the suggestion
   * trees only, and the questions the other two hold surface as known and unknown
   * in the chat instead.
   */
  const { outcomeCapabilities, allTrees } = useMemo(() => {
    if (!productTreesData?.data)
      return { outcomeCapabilities: [], allTrees: [] };

    const suggestionTrees = deduplicateTreesByQuestion(productTreesData.data);
    const situationTrees = deduplicateTreesByQuestion(
      cleanTreeNames(getTreesByMatchName(productTreesData.data, "situation-"))
    );
    const segmentTrees = deduplicateTreesByQuestion(
      cleanTreeNames(getTreesByMatchName(productTreesData.data, "segment-"))
    );

    return {
      allTrees: [...situationTrees, ...segmentTrees, ...suggestionTrees],
      outcomeCapabilities: suggestionTrees.map(formatAsOutcomeTree),
    };
  }, [productTreesData]);

  const guardrailCapabilities = useMemo(
    () => guardrailTrees?.data.map(formatAsGuardrailTree) ?? [],
    [guardrailTrees]
  );

  /**
   * Creates a lookup map for product trees by ID for efficient retrieval.
   * Uses allTrees (processed trees) to match what OutcomesList uses.
   */
  const productTreesById = useMemo(() => {
    if (allTrees.length === 0) return new Map<string, ProductTree>();
    return allTrees.reduce(
      (acc: Map<string, ProductTree>, tree: ProductTree) => {
        acc.set(tree.id, tree);
        return acc;
      },
      new Map<string, ProductTree>()
    );
  }, [allTrees]);

  const guardrailTreesById = useMemo(() => {
    if (guardrailTrees?.data.length === 0)
      return new Map<string, ApiGuardrailTree>();
    return (
      guardrailTrees?.data.reduce(
        (acc: Map<string, ApiGuardrailTree>, tree: ApiGuardrailTree) => {
          acc.set(tree.id, tree);
          return acc;
        },
        new Map<string, ApiGuardrailTree>()
      ) ?? new Map<string, ApiGuardrailTree>()
    );
  }, [guardrailTrees]);

  // Prefetch data for all known documents on load
  const prefetchGuardrailEditData = usePrefetchGuardrailEditData(
    "getGuardrailEditDataByDocument"
  );
  const prefetchProcessedClauses = usePrefetchProcessedClauseApi(
    "getProcessedClauses"
  );
  const prefetchHighlights = usePrefetchProcessedClauseApi("getHighlights");

  // Known documents that have edit data available
  const DOCUMENTS_WITH_EDIT_DATA = ["COBS4", "cp25-17", "PERG"];

  // Track if we've already prefetched to prevent re-fetching
  const hasPrefetchedRef = useRef(false);

  useEffect(() => {
    if (hasPrefetchedRef.current) return;
    hasPrefetchedRef.current = true;

    prefetchProcessedClauses(undefined, { force: true });

    for (const documentName of DOCUMENTS_WITH_EDIT_DATA) {
      const regulationId = documentName + ".pdf";
      prefetchGuardrailEditData({ documentName }, { force: true });
      prefetchHighlights({ regulationId }, { force: true });
    }
  }, [prefetchGuardrailEditData, prefetchProcessedClauses, prefetchHighlights]);

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? HOME_TAB;
  const isHomeActive = activeTab.type === "home";

  /**
   * Handles opening an item by creating a new tab if it doesn't exist.
   *
   * @param item - The overview item to open
   */
  const handleOpenGraph = (item: OverviewItem) => {
    const tabId = `${item.type}-${item.id}`;

    setTabs((currentTabs) => {
      const alreadyOpen = currentTabs.some((tab) => tab.id === tabId);

      if (alreadyOpen) {
        return currentTabs;
      }

      const nextTab = createTabFromItem(item);

      return [...currentTabs, nextTab];
    });

    setActiveTabId(tabId);
  };

  /**
   * Handles activating a specific tab.
   * Also resets the view to overview since clicking a tab indicates
   * the user wants to see tab content, not the test page.
   *
   * @param tabId - The ID of the tab to activate
   */
  const handleActivateTab = (tabId: string) => {
    guardNavigation(() => {
      setActiveTabId(tabId);
      setActiveView(VIEWS.OVERVIEW.id);
    });
  };

  /**
   * Handles closing a tab and switches to another tab if the active tab is closed.
   *
   * @param tabId - The ID of the tab to close
   */
  const handleCloseTab = (tabId: string) => {
    const isClosingActiveTab = tabId === activeTabId;

    setTabs((currentTabs) => {
      const filteredTabs = currentTabs.filter((tab) => tab.id !== tabId);

      if (isClosingActiveTab) {
        // If we're closing the active tab, switch to another tab or home
        const remainingTabs = filteredTabs.filter((tab) => tab.type !== "home");
        if (remainingTabs.length > 0) {
          // Switch to the first remaining tab (or could switch to the previous/next tab)
          setActiveTabId(remainingTabs[0].id);
        } else {
          // No other tabs, switch to home
          setActiveTabId(HOME_TAB.id);
        }
      }
      // If closing an inactive tab, don't change the active tab

      return filteredTabs;
    });
  };

  const handleNavigateHome = () => {
    guardNavigation(() => {
      setActiveTabId(HOME_TAB.id);
      setActiveView(VIEWS.OVERVIEW.id);
    });
  };

  /**
   * Handles sidebar item clicks for navigation between views.
   */
  const handleSidebarItemClick = (itemId: string) => {
    const navigate = () => {
      setPreviousConversation(undefined);
      setActiveTabId(HOME_TAB.id);

      if (itemId === VIEWS.OVERVIEW.id) {
        setActiveView(VIEWS.OVERVIEW.id);
      } else if (itemId === VIEWS.OUTCOMES.id) {
        setActiveView(VIEWS.OUTCOMES.id);
      } else if (itemId === VIEWS.GUARDRAILS.id) {
        setActiveView(VIEWS.GUARDRAILS.id);
      } else if (itemId === VIEWS.TEST.id) {
        setActiveView(VIEWS.TEST.id);
      } else if (itemId === VIEWS.HISTORY.id) {
        setActiveView(VIEWS.HISTORY.id);
      } else if (itemId === VIEWS.BENCHMARK.id) {
        setActiveView(VIEWS.BENCHMARK.id);
      }
    };

    if (itemId === VIEWS.TEST.id) {
      navigate();
    } else {
      guardNavigation(navigate);
    }
  };

  /**
   * Renders the icon for a tab based on its configuration.
   *
   * @param tab - The tab to render an icon for
   * @returns The rendered tab icon or null
   */
  const renderTabIcon = (tab: Tab) => {
    if (tab.type === "outcome") {
      return <Target className="w-4 h-4" />;
    }
    if (tab.type === "guardrail") {
      return <Shield className="w-4 h-4" />;
    }
    return <></>;
  };

  const handleNavigateToTest = (previousConversation: PreviousConversation) => {
    setActiveView(VIEWS.TEST.id);
    setActiveTabId(HOME_TAB.id);
    setPreviousConversation(previousConversation);
  };

  /**
   * Handles navigation to History page from Test page.
   * Triggers a refresh of the History page.
   */
  const handleNavigateToHistory = () => {
    guardNavigation(() => {
      setHistoryRefreshKey((prev) => prev + 1);
      setActiveView(VIEWS.HISTORY.id);
      setActiveTabId(HOME_TAB.id);
    });
  };

  const activeSidebarItem = activeView;

  /**
   * Renders the main content based on the active tab.
   */
  const handleNavigateToOutcomes = () => {
    setActiveView(VIEWS.OUTCOMES.id);
    setActiveTabId(HOME_TAB.id);
  };

  const handleNavigateToChat = () => {
    setPreviousConversation(undefined);
    setActiveView(VIEWS.TEST.id);
    setActiveTabId(HOME_TAB.id);
  };

  const renderOverview = () => (
    <OverviewSection
      outcomes={outcomeCapabilities}
      guardrails={guardrailCapabilities}
      isLoadingOutcomes={isProductTreesLoading}
      isLoadingGuardrails={isGuardrailTreesLoading}
      onNavigateToOutcomes={handleNavigateToOutcomes}
      onNavigateToChat={handleNavigateToChat}
      onNavigateToHistory={handleNavigateToHistory}
    />
  );

  const renderContent = () => {
    if (activeView === "test") {
      return (
        <TestPage2
          previousConversation={previousConversation}
          onNavigateToHistory={handleNavigateToHistory}
          onSimulationRunningChange={handleSimulationRunningChange}
          stopSimulationRef={stopSimulationRef}
        />
      );
    }
    if (activeView === "history") {
      return (
        <PastConversationsPage
          key={historyRefreshKey}
          navigateToTest={handleNavigateToTest}
        />
      );
    }
    if (activeView === "benchmark") {
      return null;
    }

    if (activeView === "overview" && activeTab.type === "home") {
      return renderOverview();
    }

    if (activeView === "outcomes" && activeTab.type === "home") {
      return (
        <OutcomesSection
          outcomes={outcomeCapabilities}
          onOpenGraph={handleOpenGraph}
          isLoadingProductTrees={isProductTreesLoading}
        />
      );
    }

    if (activeView === "guardrails" && activeTab.type === "home") {
      return (
        <GuardrailsSection
          guardrails={guardrailCapabilities}
          clauses={processedClauses?.data ?? []}
          onOpenGraph={handleOpenGraph}
          isLoadingGuardrails={isGuardrailTreesLoading}
        />
      );
    }

    if (activeTab.type === "home") {
      return renderOverview();
    }

    if (activeTab.type === "outcome" && activeTab.relatedId) {
      // Show graph view in fullscreen for outcome tabs
      // Get the product tree from the map (same approach as SystemThinkingPanel)
      const productTree = productTreesById.get(activeTab.relatedId);
      if (!productTree) {
        // Debug: Log available IDs to help diagnose the issue
        const availableIds = Array.from(productTreesById.keys()).slice(0, 10);
        const matchingByName = Array.from(productTreesById.values()).find(
          (tree) => tree.name === activeTab.relatedId
        );
        return (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-sm text-red-500 space-y-2">
              <div>Product tree not found (ID: {activeTab.relatedId}).</div>
              <div className="text-xs">
                Available IDs (first 10): {availableIds.join(", ")}
              </div>
              {matchingByName && (
                <div className="text-xs text-green-600">
                  Found by name: {matchingByName.name} (ID: {matchingByName.id})
                </div>
              )}
            </div>
          </div>
        );
      }
      // Use activeTab.id as key to ensure each tab gets its own graph instance
      // This prevents React from reusing the same component instance across tabs
      return <GraphView key={activeTab.id} tree={productTree} />;
    }

    if (activeTab.type === "guardrail" && activeTab.relatedId) {
      const guardrail = guardrailTreesById.get(activeTab.relatedId);
      if (!guardrail) {
        return (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-sm text-red-500 space-y-2">
              <div>Guardrail tree not found (ID: {activeTab.relatedId}).</div>
            </div>
          </div>
        );
      }

      // Input guardrails are shown on a canvas like outcome trees
      // Output guardrails (regulation-based) use the PDF view
      const documentName = extractDocumentName(guardrail.name);
      if (guardrail.type === "INPUT" || !documentName) {
        // Adapt guardrail tree to ProductTree shape for GraphView
        const treeForGraph = {
          id: guardrail.id,
          name: guardrail.name,
          description: "",
          preferenceRank: 0,
          template: guardrail.template,
        };
        return <GraphView key={activeTab.id} tree={treeForGraph} />;
      }

      return (
        <GuardrailContent
          key={activeTab.id}
          documentName={documentName}
          guardrailName={guardrail.name}
        />
      );
    }

    return null;
  };

  // Always show the sidebar so users can navigate between views
  const shouldHideSidebar = false;

  return (
    <TargetedSupportLayout
      isHomeActive={isHomeActive}
      onNavigateHome={handleNavigateHome}
      tabs={tabs}
      activeTabId={activeTabId}
      onActivateTab={handleActivateTab}
      onCloseTab={handleCloseTab}
      renderTabIcon={renderTabIcon}
      sidebarItems={sidebarItems}
      activeSidebarItem={activeSidebarItem}
      sidebarIcons={
        sidebarIcons as Record<
          string,
          React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
        >
      }
      onSidebarItemClick={handleSidebarItemClick}
      fullWidth={true}
      hideSidebar={shouldHideSidebar}
    >
      {renderContent()}
      {isAdmin && (
        <div
          style={{ display: activeView === "benchmark" ? "contents" : "none" }}
        >
          <BenchmarkPage navigateToTest={handleNavigateToTest} />
        </div>
      )}
      <ConfirmDialog
        open={!!pendingNavAction}
        title="Leave simulation?"
        description="A simulation is currently running. If you leave, it will be cancelled."
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </TargetedSupportLayout>
  );
};
